/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-useless-escape */
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaSpinner, FaUserPlus, FaSignInAlt, FaCalendarAlt } from "react-icons/fa";
import axios from "axios";

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [settings, setSettings] = useState(null);
  const [mainLogo, setMainLogo] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [error, setError] = useState(null);

  // Active programs from multiple APIs
  const [activePrograms, setActivePrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);

  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const CHAPTERS_API = `${process.env.REACT_APP_API_BASE_URL}/UIchapters`;
  const SETTINGS_API = `${process.env.REACT_APP_API_BASE_URL}/settings/ui`;
  const MEDIA_API = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;

  // APIs for programs
  const ALL_PROGRAMS_API = `${process.env.REACT_APP_API_BASE_URL}/UI/all-programs`;
  const FINANCIAL_WORKSHOP_API = `${process.env.REACT_APP_API_BASE_URL}/UI/all-financialWorkshop`;

  // Fetch settings and main logo from API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoadingSettings(true);
        const response = await axios.get(SETTINGS_API);
        const apiData = response.data;

        // Find the active setting
        let setting = null;

        if (apiData.results && Array.isArray(apiData.results)) {
          setting = apiData.results[0] || apiData.results.find(s => s.isActive);
        } else if (Array.isArray(apiData)) {
          setting = apiData[0] || apiData.find(s => s.isActive);
        } else if (typeof apiData === 'object') {
          setting = apiData;
        }

        if (setting) {
          setSettings(setting);

          // Fetch main logo if available
          if (setting.mainWebsiteLogoMediaId) {
            try {
              const mediaId = typeof setting.mainWebsiteLogoMediaId === 'object'
                ? setting.mainWebsiteLogoMediaId.id || setting.mainWebsiteLogoMediaId._id
                : setting.mainWebsiteLogoMediaId;

              if (mediaId) {
                const logoResponse = await axios.get(`${MEDIA_API}/${mediaId}/download`, {
                  responseType: "blob",
                });

                const logoUrl = URL.createObjectURL(logoResponse.data);
                setMainLogo(logoUrl);
              }
            } catch (logoError) {
              console.error("Error fetching main logo:", logoError);
              setMainLogo(null);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();

    // Cleanup function for blob URL
    return () => {
      if (mainLogo) {
        URL.revokeObjectURL(mainLogo);
      }
    };
  }, []);

  // Fetch chapters from API
  useEffect(() => {
    const fetchChapters = async () => {
      try {
        setLoadingChapters(true);
        setError(null);
        const response = await axios.get(CHAPTERS_API);

        // Filter only active chapters and sort by name
        const activeChapters = response.data
          .filter(chapter => chapter.isActive === true)
          .sort((a, b) => a.chapterName.localeCompare(b.chapterName));

        setChapters(activeChapters);
      } catch (err) {
        console.error("Error fetching chapters:", err);
        setError("Failed to load chapters");
      } finally {
        setLoadingChapters(false);
      }
    };

    fetchChapters();
  }, [CHAPTERS_API]);

  // SINGLE TIME API CALL FOR ALL ACTIVE PROGRAMS
/* ===============================
   SINGLE TIME API CALL FOR ALL PROGRAMS (ACTIVE + INACTIVE)
=============================== */
useEffect(() => {
  const fetchAllPrograms = async () => {
    try {
      setLoadingPrograms(true);

      // console.log("📡 Fetching ALL programs and workshops (including inactive)...");

      const [programsResponse, workshopResponse] = await Promise.all([
        axios.get(ALL_PROGRAMS_API),
        axios.get(FINANCIAL_WORKSHOP_API)
      ]);

      let allPrograms = [];

      /* ===============================
         REGULAR PROGRAMS (ALL - ACTIVE + INACTIVE)
      =============================== */
      if (programsResponse?.data?.success) {
        const programsData = programsResponse.data.data || [];

        if (Array.isArray(programsData)) {
          const regularPrograms = programsData.map(program => {
            const programName =
              program.programInfo?.programName ||
              program.programInfo?.title ||
              program.title ||
              program.name ||
              "Program";

            return {
              id: program._id || program.id,
              name: programName.trim(),
              data: program,
              source: "all-programs",
              apiType: "all-programs",
              isActive: Boolean(program.isActive), // Store status
              status: program.isActive ? "Active" : "Inactive" // Human readable status
            };
          });

          allPrograms.push(...regularPrograms);
          // console.log(`✅ Added ${regularPrograms.length} programs (active + inactive)`);
        }
      }

      /* ===============================
         FINANCIAL WORKSHOPS (ALL - ACTIVE + INACTIVE)
      =============================== */
      if (workshopResponse?.data) {
        let workshopData = workshopResponse.data;

        // Check if data is nested
        if (workshopData.success && workshopData.data) {
          workshopData = workshopData.data;
        }

        // Handle array of workshops
        if (Array.isArray(workshopData)) {
          const workshopArray = workshopData.map(workshop => {
            const workshopName =
              workshop.workshopInfo?.programName ||
              workshop.workshopInfo?.title ||
              workshop.title ||
              workshop.name ||
              "Financial Workshop";

            return {
              id: workshop._id || workshop.id || `financial-workshop-${Date.now()}`,
              name: workshopName.trim(),
              data: workshop,
              source: "financialWorkshop",
              apiType: "financial-workshop",
              isActive: Boolean(workshop.isActive),
              status: workshop.isActive ? "Active" : "Inactive"
            };
          });

          allPrograms.push(...workshopArray);
          // console.log(`✅ Added ${workshopArray.length} workshops (active + inactive)`);
        }
        // Handle single workshop object
        else if (typeof workshopData === 'object' && workshopData !== null) {
          const workshopName =
            workshopData.workshopInfo?.programName ||
            workshopData.workshopInfo?.title ||
            workshopData.title ||
            workshopData.name ||
            "Financial Workshop";

          allPrograms.push({
            id: workshopData._id || workshopData.id || "financial-workshop",
            name: workshopName.trim(),
            data: workshopData,
            source: "financialWorkshop",
            apiType: "financial-workshop",
            isActive: Boolean(workshopData.isActive),
            status: workshopData.isActive ? "Active" : "Inactive"
          });

          // console.log("✅ Added financial workshop (single object)");
        }
      }

      /* ===============================
         REMOVE DUPLICATES
      =============================== */
      const uniquePrograms = [];
      const seenIds = new Set();

      allPrograms.forEach(item => {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          uniquePrograms.push(item);
        }
      });

      /* ===============================
         SORT BY NAME
      =============================== */
      uniquePrograms.sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      /* ===============================
         LOG DETAILS FOR DEBUGGING
      =============================== */
      // console.log(`🎯 Total programs (all): ${uniquePrograms.length}`);
      
      // Count active vs inactive
      const activeCount = uniquePrograms.filter(p => p.isActive).length;
      const inactiveCount = uniquePrograms.filter(p => !p.isActive).length;
      
      // console.log(`📊 Active: ${activeCount}, Inactive: ${inactiveCount}`);
      
      // Log each program with status
      uniquePrograms.forEach(program => {
        // console.log(`📝 ${program.name} - ${program.status} (${program.source})`);
      });

      /* ===============================
         SET STATE
      =============================== */
      const onlyActivePrograms = uniquePrograms.filter(
  program => program.isActive === true
);

setActivePrograms(onlyActivePrograms);

    } catch (error) {
      console.error("❌ Error fetching programs:", error);
      setActivePrograms([]);
    } finally {
      setLoadingPrograms(false);
    }
  };

  fetchAllPrograms();
}, [ALL_PROGRAMS_API, FINANCIAL_WORKSHOP_API]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Toggle mobile menu
  const toggleMenu = () => {
    setMenuOpen((prev) => {
      if (prev) setDropdownOpen(null);
      return !prev;
    });
  };

  // Toggle dropdown menus
  const toggleDropdown = (menu) => {
    setDropdownOpen((prev) => (prev === menu ? null : menu));
  };

  // Handle nav item clicks
  const handleNavItemClick = () => {
    if (window.innerWidth < 1024) {
      setMenuOpen(false);
      setDropdownOpen(null);
    }
  };

  // Handle program click
  const handleProgramClick = (programId, programName, apiType = 'all-programs') => {
    handleNavItemClick();
    
    let formattedName = 'program';
    
    if (programName && typeof programName === 'string' && programName.trim() !== '') {
      formattedName = programName
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
    }
    
    if (apiType === 'financial-workshop') {
      navigate(`/workshop/${formattedName}`, {
        state: { 
          programId,
          apiType
        }
      });
    } else {
      navigate(`/programme/${formattedName}`, {
        state: { 
          programId,
          apiType
        }
      });
    }
  };

  // Handle mobile Register button click
  const handleRegisterClick = () => {
    handleNavItemClick();
    navigate("/membership");
  };

  // Format chapter name for URL
  const formatChapterUrl = (chapterName) => {
    return chapterName
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };

  // Show loading state for logo
  if (loadingSettings) {
    return (
      <div className="bg-gray-900 text-white p-4 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-0 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 bg-gray-800 animate-pulse rounded"></div>
            <div className="h-6 w-32 bg-gray-800 animate-pulse rounded"></div>
          </div>
          <div className="lg:hidden">
            <div className="h-8 w-8 bg-gray-800 animate-pulse rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white p-4 sticky top-0 z-50">
      <div className="max-w-[100%] mx-auto px-0 flex flex-col xl:flex-row xl:items-center">
        {/* Logo and hamburger */}
        <div className="flex justify-between items-center w-full xl:w-auto">
          <Link to="/" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
            {/* Dynamic Main Logo */}
            {mainLogo ? (
              <img
                src={mainLogo}
                alt={`${settings?.websiteName || "MASA Forum"} Logo`}
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  console.error("Failed to load main logo");
                  e.target.style.display = 'none';
                  const fallbackDiv = e.target.parentElement.querySelector('.logo-fallback');
                  if (fallbackDiv) fallbackDiv.style.display = 'flex';
                }}
              />
            ) : (
              <div className="logo-fallback h-10 w-10 bg-gradient-to-r from-blue-500 to-teal-400 rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
            )}

            {/* Dynamic Website Name */}
            <span className="text-xl font-bold whitespace-nowrap">
              {settings?.websiteName || "MASA Forum"}
            </span>
          </Link>
          <button
            className="xl:hidden text-white text-2xl"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {/* Navigation content */}
        <nav
          className={`flex flex-col xl:flex-row xl:items-center xl:flex-1 w-full transition-all duration-300 ease-in-out ${menuOpen ? "block mt-4" : "hidden xl:flex"
            }`}
          ref={dropdownRef}
        >
          <div className="flex flex-col xl:flex-row xl:items-center xl:ml-2 2xl:ml-12 w-full xl:w-auto bg-gray-800 xl:bg-transparent">
            {/* About Dropdown */}
            <div className="relative">
              <button
                className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 w-full xl:w-auto text-left text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold flex justify-between items-center xl:justify-start whitespace-nowrap"
                onClick={() => toggleDropdown("about")}
              >
                About
                <span className="xl:hidden">{dropdownOpen === "about" ? "−" : "+"}</span>
              </button>
              <div
                className={`xl:absolute bg-white text-black shadow-lg rounded-lg p-2 min-w-[150px] transform transition-all duration-200 ease-in-out origin-top z-50 ${dropdownOpen === "about"
                  ? "max-h-96 opacity-100 scale-y-100"
                  : "max-h-0 opacity-0 scale-y-0 pointer-events-none overflow-hidden"
                  } xl:mt-2 mt-0 left-0`}
              >
                <Link
                  to="/about"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Mission/Vision
                </Link>
                <Link
                  to="/team"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Our Team
                </Link>
                <Link
                  to="/partners"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Our Partners
                </Link>
                <Link
                  to="/ourmembers"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Our Members
                </Link>
              </div>
            </div>

            {/* Our Initiatives Dropdown */}
            <div className="relative">
              <button
                className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 w-full xl:w-auto text-left text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold flex justify-between items-center xl:justify-start whitespace-nowrap"
                onClick={() => toggleDropdown("initiatives")}
              >
                Our Initiatives
                <span className="xl:hidden">{dropdownOpen === "initiatives" ? "−" : "+"}</span>
              </button>
              <div
                className={`xl:absolute bg-white text-black shadow-lg rounded-lg p-2 min-w-[180px] transform transition-all duration-200 ease-in-out origin-top z-50 ${dropdownOpen === "initiatives"
                  ? "max-h-96 opacity-100 scale-y-100"
                  : "max-h-0 opacity-0 scale-y-0 pointer-events-none overflow-hidden"
                  } xl:mt-2 mt-0 left-0`}
              >
                <Link
                  to="/MSMEKalptaru"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  MSME Kalptaru
                </Link>
                <Link
                  to="/MyPowerBrand"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  My Power Brand
                </Link>
                <Link
                  to="/MSMETimes"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  MSME Times
                </Link>
                <Link
                  to="/AccelerationProgram"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Acceleration Program
                </Link>
                <Link
                  to="/NiveshManthan"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Nivesh Manthan
                </Link>

                {/* Workshop & Program with submenu */}
                <div className="relative group">
                  <div className="flex justify-between items-center px-4 py-2 hover:bg-yellow-500 cursor-default">
                    <span className="font-bold text-sm">Workshop & Program</span>
                    <span className="text-xs">›</span>
                  </div>

                  {/* Program Submenu */}
                  <div className="absolute  top-15 bg-white shadow-lg rounded-lg p-2 min-w-[250px] max-h-[300px] overflow-y-auto opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ml-1">
                    <div className="text-xs text-gray-500 px-2 py-1 font-semibold border-b mb-1">
                      Active Programs ({activePrograms.length})
                    </div>

                    {loadingPrograms ? (
                      <div className="px-3 py-2 flex items-center gap-2">
                        <FaSpinner className="animate-spin text-gray-400 text-xs" />
                        <span className="text-xs text-gray-600">Loading programs...</span>
                      </div>
                    ) : activePrograms.length > 0 ? (
                      <div className="max-h-[250px] overflow-y-auto">
                        {activePrograms.map((program, index) => (
                          <button
                            key={program.id || index}
                            onClick={() => handleProgramClick(program.id, program.name, program.apiType)}
                            className="block w-full text-left px-3 py-2 hover:bg-yellow-500 hover:text-white font-bold text-sm flex items-center gap-2 border-b last:border-b-0 transition-colors"
                            title={program.name}
                          >
                            <FaCalendarAlt className={`text-xs flex-shrink-0 ${program.apiType === 'financial-workshop' ? 'text-green-500' : 'text-blue-500'}`} />
                            <span className="truncate">{program.name}</span>
                            {program.source === 'financialWorkshop' && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded ml-auto">
                                Workshop
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-2 text-xs text-gray-600">
                        No active programs
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/MASAAwards"
              className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold whitespace-nowrap"
              onClick={handleNavItemClick}
            >
              MASA Awards
            </Link>

            <Link
              to="/membership"
              className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold whitespace-nowrap"
              onClick={handleNavItemClick}
            >
              Membership
            </Link>
            <Link
              to="/events"
              className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold whitespace-nowrap"
              onClick={handleNavItemClick}
            >
              Events
            </Link>

            {/* MASA Chapters Dropdown */}
            <div className="relative">
              <button
                className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 w-full xl:w-auto text-left text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold flex justify-between items-center xl:justify-start whitespace-nowrap"
                onClick={() => toggleDropdown("chapters")}
              >
                MASA Chapters
                <span className="xl:hidden">{dropdownOpen === "chapters" ? "−" : "+"}</span>
              </button>
              <div
                className={`xl:absolute bg-white text-black shadow-lg rounded-lg p-2 min-w-[200px] transform transition-all duration-200 ease-in-out origin-top z-50 ${dropdownOpen === "chapters"
                  ? "max-h-96 opacity-100 scale-y-100"
                  : "max-h-0 opacity-0 scale-y-0 pointer-events-none overflow-hidden"
                  } xl:mt-2 mt-0 left-0`}
              >
                {loadingChapters ? (
                  <div className="px-4 py-2 flex items-center gap-2">
                    <FaSpinner className="animate-spin text-gray-400" />
                    <span className="text-sm text-gray-600">Loading chapters...</span>
                  </div>
                ) : error ? (
                  <div className="px-4 py-2">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                ) : chapters.length === 0 ? (
                  <div className="px-4 py-2">
                    <p className="text-sm text-gray-600">No chapters available</p>
                  </div>
                ) : (
                  chapters.map((chapter) => (
                    <Link
                      key={chapter.id || chapter._id}
                      to={`/chapters/${formatChapterUrl(chapter.chapterName)}`}
                      state={{ chapterId: chapter.id || chapter._id }}
                      className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm whitespace-nowrap"
                      onClick={handleNavItemClick}
                    >
                      {chapter.chapterName} Chapter
                      {chapter.isActive === false && (
                        <span className="ml-2 text-xs text-red-500">(Inactive)</span>
                      )}
                    </Link>
                  ))
                )}
              </div>
            </div>

            {/* Resources Dropdown */}
            <div className="relative">
              <button
                className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 w-full xl:w-auto text-left text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold flex justify-between items-center xl:justify-start whitespace-nowrap"
                onClick={() => toggleDropdown("resources")}
              >
                Resources
                <span className="xl:hidden">{dropdownOpen === "resources" ? "−" : "+"}</span>
              </button>
              <div
                className={`xl:absolute bg-white text-black shadow-lg rounded-lg p-2 min-w-[150px] transform transition-all duration-200 ease-in-out origin-top z-50 ${dropdownOpen === "resources"
                  ? "max-h-96 opacity-100 scale-y-100"
                  : "max-h-0 opacity-0 scale-y-0 pointer-events-none overflow-hidden"
                  } xl:mt-2 mt-0 left-0`}
              >
                <Link
                  to="/BlogsPage"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Blogs
                </Link>
                <Link
                  to="/MSMENews"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  MSME Times
                </Link>
                <Link
                  to="/SchemesPage"
                  className="block px-4 py-2 hover:bg-yellow-500 font-bold text-sm"
                  onClick={handleNavItemClick}
                >
                  Schemes
                </Link>
              </div>
            </div>

            <Link
              to="/MasainMedia"
              className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold whitespace-nowrap"
              onClick={handleNavItemClick}
            >
              MASA in Media
            </Link>
            <Link
              to="/ContactUs"
              className="hover:text-yellow-500 xl:px-1.5 2xl:px-3 py-2 text-[0.95rem] xl:text-[0.8rem] 2xl:text-[0.95rem] font-bold whitespace-nowrap"
              onClick={handleNavItemClick}
            >
              Contact Us
            </Link>

            {/* Mobile Register Button */}
            <div className={`xl:hidden px-3 py-2 ${menuOpen ? "block" : "hidden"}`}>
              <button
                className="block bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2.5 rounded-lg hover:from-orange-500 hover:to-amber-500 font-black text-[0.85rem] w-full mb-3 border-2 border-amber-400"
                onClick={handleRegisterClick}
              >
                JOIN NOW
              </button>

              {/* Mobile Member Login */}
              <a
                href="https://masaforum.com/manage/member"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2.5 rounded-lg hover:from-orange-500 hover:to-amber-500 font-black text-[0.85rem] w-full border-2 border-amber-400"
                onClick={handleNavItemClick}
              >
                MEMBER LOGIN
              </a>
            </div>
          </div>

          {/* Desktop Buttons */}
          <div className="hidden xl:flex xl:items-center xl:ml-auto gap-1.5 2xl:gap-3">
            <Link
              to="/membership"
              className="flex items-center gap-1.5 2xl:gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white xl:px-3 xl:py-2 2xl:px-5 2xl:py-2.5 rounded-lg hover:from-orange-500 hover:to-amber-500 font-black xl:text-[0.75rem] 2xl:text-[0.85rem] whitespace-nowrap transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg border-2 border-amber-400"
              onClick={handleNavItemClick}
            >
              <FaUserPlus className="text-base animate-bounce" />
              <span>JOIN</span>
            </Link>

            <a
              href="https://masaforum.com/manage/member"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 2xl:gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white xl:px-3 xl:py-2 2xl:px-5 2xl:py-2.5 rounded-lg hover:from-orange-500 hover:to-amber-500 font-black xl:text-[0.75rem] 2xl:text-[0.85rem] whitespace-nowrap transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg border-2 border-amber-400"
              onClick={handleNavItemClick}
            >
              <FaSignInAlt className="text-base" />
              <span>LOGIN</span>
            </a>
          </div>
        </nav>
      </div>
    </div>
  );
}

export default Navbar;